// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import type {
  CanonicalProjectEvent,
  ProjectedLegacyStep,
  ProjectedRun,
  ProjectViewState,
} from '@web/lib/projector';
import {
  presentLegacyActivity,
  type ActivityItemKind,
} from '@web/lib/remoteActivityRegistry';
import {
  buildAgentExecutionForest,
  type AgentExecutionForest,
  type RemoteExecutionMode,
} from '@web/lib/remoteAgentProjection';
import type { RemoteCommandDisplay } from '@web/lib/remoteCommandProjection';
import {
  hasVersionedWorkItemsForRun,
  selectAgentExecutionsForRun,
  selectChatTurn,
  selectFinalAnswer,
  selectPendingHumanInputs,
  selectWorkItemsForRun,
  type AgentExecutionView,
  type AgentMessageItem,
  type ChatTurnView,
  type HumanInputItem,
  type UnknownWorkItem,
  type WorkItem,
} from '@web/lib/remoteWorkItemModel';

export type ActivitySyncState = 'live' | 'catching_up' | 'partial';

export type ActivityRunStatus =
  | 'running'
  | 'waiting_input'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'interrupted';

export type ActivityItemState =
  | 'completed'
  | 'active'
  | 'waiting'
  | 'failed'
  | 'interrupted'
  | 'neutral';

export type ActivityItem = {
  id: string;
  type: string;
  kind: ActivityItemKind;
  state: ActivityItemState;
  title: string;
  detail: string;
  timestamp: string | null;
  eventIds: string[];
  sequence?: number;
  payload: unknown;
  source: ProjectedLegacyStep['source'];
};

export type ActivityRun = {
  id: string;
  version: number;
  mode: RemoteExecutionMode;
  title: string;
  status: ActivityRunStatus;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  updatedAt: string | null;
  userIntent: ActivityItem | null;
  items: ActivityItem[];
  agentForest: AgentExecutionForest;
  runLevelItems: ActivityItem[];
  result: ActivityItem | null;
  /** Versioned PR #3 work items; legacy rendering remains available below. */
  normalizedTurn: ChatTurnView | null;
  typedWorkItems: WorkItem[];
  /** Unsupported future-schema items shown without exposing raw payloads. */
  unsupportedWorkItems: UnknownWorkItem[];
  typedWorkItemsById: Record<string, WorkItem>;
  agentExecutionsById: Record<string, AgentExecutionView>;
  rootExecutionKeys: string[];
  finalAnswer: AgentMessageItem | null;
  pendingHumanInputs: HumanInputItem[];
  currentSummary: string | null;
  currentAgentName: string | null;
  activeAgentCount: number;
};

export type ActivityTimelineModel = {
  projectId: string;
  sync: {
    state: ActivitySyncState;
    currentCursor: number;
    lastSyncedAt: string | null;
    detail: string | null;
  };
  runs: ActivityRun[];
  activeRunId: string | null;
};

type MutableActivityRun = {
  id: string;
  aggregate: ProjectedRun | null;
  steps: ProjectedLegacyStep[];
};

const FAILED_STEPS = new Set([
  'error',
  'failed',
  'budget_not_enough',
  'context_too_long',
]);

const CLOSED_ASK_STEPS = new Set([
  'human_reply',
  'end',
  'wait_confirm',
  ...FAILED_STEPS,
]);

function timestampIso(value: number | null): string | null {
  if (value === null || !Number.isFinite(value)) return null;
  const date = new Date(value < 10_000_000_000 ? value * 1000 : value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function shortTitle(value: string, fallback: string): string {
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (!normalized) return fallback;
  return normalized.length > 72 ? `${normalized.slice(0, 71)}…` : normalized;
}

function hasOpenAsk(steps: ProjectedLegacyStep[]): boolean {
  let open = false;
  for (const step of steps) {
    if (step.step === 'ask') open = true;
    else if (CLOSED_ASK_STEPS.has(step.step)) open = false;
  }
  return open;
}

function fallbackRunStatus(steps: ProjectedLegacyStep[]): ActivityRunStatus {
  const last = steps[steps.length - 1];
  if (!last) return 'running';
  if (FAILED_STEPS.has(last.step)) return 'failed';
  if (last.step === 'end' || last.step === 'wait_confirm') return 'completed';
  return 'running';
}

function activityRunStatus(
  aggregate: ProjectedRun | null,
  steps: ProjectedLegacyStep[]
): ActivityRunStatus {
  if (aggregate && aggregate.status !== 'running') return aggregate.status;
  if (hasOpenAsk(steps)) return 'waiting_input';
  return aggregate?.status || fallbackRunStatus(steps);
}

function initialItemState(
  step: ProjectedLegacyStep,
  kind: ActivityItemKind
): ActivityItemState {
  if (FAILED_STEPS.has(step.step)) return 'failed';
  if (kind === 'question') return 'waiting';
  if (kind === 'user') return 'neutral';
  return 'completed';
}

function activityItem(
  step: ProjectedLegacyStep,
  commandContent?: string
): ActivityItem {
  const presentation = presentLegacyActivity(step.step, step.data);
  return {
    id: `${step.taskId}:${String(step.stepId)}`,
    type: step.step,
    kind: presentation.kind,
    state: initialItemState(step, presentation.kind),
    title: presentation.title,
    detail: presentation.detail || commandContent || '',
    timestamp: timestampIso(step.timestamp),
    eventIds: Array.from(
      new Set([step.eventId, ...(step.crossLaneEventIds || [])])
    ),
    sequence: step.runSequence > 0 ? step.runSequence : undefined,
    payload: step.data,
    source: step.source,
  };
}

function markActiveRunLevelItem(
  items: ActivityItem[],
  status: ActivityRunStatus
): ActivityItem[] {
  if (status !== 'running') return items;
  const activeIndex = items.findLastIndex(
    (item) =>
      item.kind === 'agent' ||
      item.kind === 'tool' ||
      item.kind === 'progress' ||
      item.kind === 'system'
  );
  if (activeIndex < 0) return items;
  const candidate = items[activeIndex];
  if (
    candidate.type === 'deactivate_agent' ||
    candidate.type === 'deactivate_toolkit' ||
    candidate.type === 'tool_finished'
  ) {
    return items;
  }
  return items.map((item, index) =>
    index === activeIndex ? { ...item, state: 'active' } : item
  );
}

function buildRun(
  run: MutableActivityRun,
  runIndex: number,
  commandContentByStepId: Map<string, string>,
  canonicalEvents: CanonicalProjectEvent[],
  view: ProjectViewState,
  mode: RemoteExecutionMode
): ActivityRun {
  const projection = view.workItemProjection;
  const workItemsForRun = projection
    ? selectWorkItemsForRun(projection, run.id)
    : [];
  const versionedItemsForRun = workItemsForRun.filter(
    (item) => item.schemaVersion === 1
  );
  const normalizedRunVersion = versionedItemsForRun.length
    ? Math.max(...versionedItemsForRun.map((item) => item.runVersion))
    : null;
  const projectedTurn =
    projection && normalizedRunVersion !== null
      ? selectChatTurn(projection, run.id, normalizedRunVersion)
      : null;
  const useNormalized = Boolean(
    projection && projectedTurn && normalizedRunVersion !== null
  );
  const normalizedTurn = useNormalized ? projectedTurn : null;
  const aggregateStatus = activityRunStatus(run.aggregate, run.steps);
  const normalizedFinalAnswer = useNormalized
    ? selectFinalAnswer(projection, run.id, normalizedTurn?.runVersion)
    : null;
  const aggregateTerminal =
    aggregateStatus !== 'running' && aggregateStatus !== 'waiting_input';
  const pendingHumanInputs =
    useNormalized && !aggregateTerminal && !normalizedFinalAnswer
      ? selectPendingHumanInputs(
          projection,
          run.id,
          normalizedTurn?.runVersion,
          1
        )
      : [];
  let status: ActivityRunStatus = aggregateStatus;
  if (pendingHumanInputs.length) {
    status = 'waiting_input';
  } else if (
    useNormalized &&
    normalizedTurn &&
    (aggregateStatus === 'running' || aggregateStatus === 'waiting_input')
  ) {
    status =
      normalizedTurn.status === 'queued' ||
      normalizedTurn.status === 'in_progress'
        ? 'running'
        : normalizedTurn.status === 'waiting'
          ? 'waiting_input'
          : normalizedTurn.status;
  }
  const projectedItems = run.steps.map((step) =>
    activityItem(
      step,
      commandContentByStepId.get(`${step.taskId}:${String(step.stepId)}`)
    )
  );
  const firstUserIndex = projectedItems.findIndex(
    (item) => item.type === 'confirmed'
  );
  const userIntent =
    firstUserIndex >= 0 ? projectedItems[firstUserIndex] : null;
  let items = projectedItems.filter((_, index) => index !== firstUserIndex);
  const resultIndex = items.findLastIndex((item) => item.kind === 'result');
  const result = resultIndex >= 0 ? items[resultIndex] : null;
  if (resultIndex >= 0)
    items = items.filter((_, index) => index !== resultIndex);
  if (status !== 'waiting_input') {
    items = items.map((item) =>
      item.kind === 'question' && item.state === 'waiting'
        ? { ...item, state: 'completed' }
        : item
    );
  }
  const agentForest = useNormalized
    ? {
        mode,
        rootIds: [],
        nodesById: {},
        messagesById: {},
        toolsById: {},
        unassignedItemIds: items.map((item) => item.id),
      }
    : buildAgentExecutionForest({
        runId: run.id,
        mode,
        runStatus: status,
        items,
        canonicalEvents,
      });
  const runLevelItemIds = new Set(agentForest.unassignedItemIds);
  const runLevelItems = markActiveRunLevelItem(
    items.filter((item) => runLevelItemIds.has(item.id)),
    status
  );
  const runLevelItemsById = new Map(
    runLevelItems.map((item) => [item.id, item])
  );
  items = items.map((item) => runLevelItemsById.get(item.id) || item);

  const firstTimestamp = projectedItems.find(
    (item) => item.timestamp
  )?.timestamp;
  const lastTimestamp = [...projectedItems]
    .reverse()
    .find((item) => item.timestamp)?.timestamp;
  const fallbackTitle = `Run ${runIndex + 1}`;
  const typedWorkItems = useNormalized
    ? versionedItemsForRun
        .filter((item) => item.runVersion === normalizedTurn?.runVersion)
        .sort(
          (left, right) =>
            left.firstSequence - right.firstSequence ||
            left.key.localeCompare(right.key)
        )
    : [];
  const typedWorkItemsById = Object.fromEntries(
    typedWorkItems.map((item) => [item.key, item])
  );
  const typedWorkItemKeys = new Set(typedWorkItems.map((item) => item.key));
  const unsupportedWorkItems = workItemsForRun.filter(
    (item): item is UnknownWorkItem =>
      item.type === 'unknown' && !typedWorkItemKeys.has(item.key)
  );
  const agentExecutionsById = useNormalized
    ? Object.fromEntries(
        selectAgentExecutionsForRun(
          projection,
          run.id,
          normalizedTurn?.runVersion || 0
        ).map((agent) => [agent.key, agent])
      )
    : {};
  const finalAnswer = normalizedFinalAnswer;
  const completedAt =
    normalizedTurn?.completedAt ||
    (status === 'running' || status === 'waiting_input'
      ? null
      : run.aggregate?.updatedAt || lastTimestamp || null);
  const startedAt = normalizedTurn?.startedAt || firstTimestamp || null;
  const durationMs =
    normalizedTurn?.durationMs ??
    (startedAt && completedAt
      ? Math.max(0, Date.parse(completedAt) - Date.parse(startedAt))
      : null);

  return {
    id: run.id,
    version: run.aggregate?.runVersion || 0,
    mode,
    title: shortTitle(userIntent?.detail || '', fallbackTitle),
    status,
    startedAt,
    completedAt,
    durationMs,
    updatedAt: run.aggregate?.updatedAt || lastTimestamp || null,
    userIntent,
    items,
    agentForest,
    runLevelItems,
    result,
    normalizedTurn: useNormalized ? normalizedTurn : null,
    typedWorkItems,
    unsupportedWorkItems,
    typedWorkItemsById,
    agentExecutionsById,
    rootExecutionKeys: useNormalized
      ? normalizedTurn?.rootExecutionKeys || []
      : [],
    finalAnswer,
    pendingHumanInputs,
    currentSummary: useNormalized
      ? aggregateTerminal
        ? finalAnswer?.text || null
        : normalizedTurn?.currentSummary || null
      : null,
    currentAgentName:
      useNormalized && !aggregateTerminal
        ? normalizedTurn?.currentAgentName || null
        : null,
    activeAgentCount:
      useNormalized && !aggregateTerminal
        ? normalizedTurn?.activeAgentCount || 0
        : 0,
  };
}

function commandContentMap(
  steps: ProjectedLegacyStep[],
  commands: RemoteCommandDisplay[]
): Map<string, string> {
  const confirmedSteps = steps.filter((step) => step.step === 'confirmed');
  const userCommands = commands.filter(
    (command) => command.type === 'user_message'
  );
  const result = new Map<string, string>();
  const usedCommandIds = new Set<string>();

  for (const step of confirmedSteps) {
    const payload =
      step.data && typeof step.data === 'object' && !Array.isArray(step.data)
        ? (step.data as Record<string, unknown>)
        : {};
    const commandId =
      typeof payload.command_id === 'string' ? payload.command_id : null;
    const clientRequestId =
      typeof payload.client_request_id === 'string'
        ? payload.client_request_id
        : null;
    const matched = userCommands.find(
      (command) =>
        !usedCommandIds.has(command.id) &&
        ((commandId && command.id === commandId) ||
          (clientRequestId && command.clientRequestId === clientRequestId))
    );
    if (matched?.content) {
      result.set(`${step.taskId}:${String(step.stepId)}`, matched.content);
      usedCommandIds.add(matched.id);
    }
  }

  // A run id is less specific because one run can have several control or
  // reply commands. Use it only when the unmatched relationship is one-to-one.
  const stepsWithoutExactMatch = confirmedSteps.filter(
    (step) => !result.has(`${step.taskId}:${String(step.stepId)}`)
  );
  for (const step of stepsWithoutExactMatch) {
    const commandsForRun = userCommands.filter(
      (command) =>
        !usedCommandIds.has(command.id) &&
        Boolean(command.runId) &&
        command.runId === step.taskId
    );
    const stepsForRun = stepsWithoutExactMatch.filter(
      (candidate) => candidate.taskId === step.taskId
    );
    if (commandsForRun.length !== 1 || stepsForRun.length !== 1) continue;
    const matched = commandsForRun[0];
    if (matched.content) {
      result.set(`${step.taskId}:${String(step.stepId)}`, matched.content);
      usedCommandIds.add(matched.id);
    }
  }

  // Rolling deployments may not expose correlation ids yet. Apply the old
  // positional heuristic only to still-unmatched commands and steps.
  const unmatchedSteps = confirmedSteps.filter(
    (step) => !result.has(`${step.taskId}:${String(step.stepId)}`)
  );
  const unmatchedCommands = userCommands.filter(
    (command) => !usedCommandIds.has(command.id)
  );
  const stepOffset = Math.max(
    0,
    unmatchedSteps.length - unmatchedCommands.length
  );
  const commandOffset = Math.max(
    0,
    unmatchedCommands.length - unmatchedSteps.length
  );
  unmatchedSteps.slice(stepOffset).forEach((step, index) => {
    const command = unmatchedCommands[commandOffset + index];
    if (command?.content) {
      result.set(`${step.taskId}:${String(step.stepId)}`, command.content);
    }
  });
  return result;
}

export function buildActivityTimeline(
  view: ProjectViewState,
  commands: RemoteCommandDisplay[] = [],
  mode: RemoteExecutionMode = 'single-agent'
): ActivityTimelineModel {
  const runOrder: string[] = [];
  const grouped = new Map<string, MutableActivityRun>();
  const canonicalEventsByRun = new Map<string, CanonicalProjectEvent[]>();
  const normalizedRunIds = new Set(
    Object.keys(view.runs).filter(
      (runId) =>
        view.workItemProjection &&
        hasVersionedWorkItemsForRun(view.workItemProjection, runId)
    )
  );

  const ensureRun = (runId: string): MutableActivityRun => {
    const existing = grouped.get(runId);
    if (existing) return existing;
    const created = {
      id: runId,
      aggregate: view.runs[runId] || null,
      steps: [],
    };
    grouped.set(runId, created);
    runOrder.push(runId);
    return created;
  };

  for (const step of view.legacySteps) {
    ensureRun(step.taskId).steps.push(step);
  }
  if (normalizedRunIds.size < Object.keys(view.runs).length) {
    for (const event of view.canonicalEvents || []) {
      if (normalizedRunIds.has(event.runId)) continue;
      const events = canonicalEventsByRun.get(event.runId) || [];
      events.push(event);
      canonicalEventsByRun.set(event.runId, events);
    }
  }
  Object.keys(view.runs).forEach(ensureRun);

  const contentByStepId = commandContentMap(view.legacySteps, commands);
  const runs = runOrder.map((runId, index) =>
    buildRun(
      grouped.get(runId)!,
      index,
      contentByStepId,
      canonicalEventsByRun.get(runId) || [],
      view,
      mode
    )
  );
  const activeRun = [...runs]
    .reverse()
    .find((run) => run.status === 'running' || run.status === 'waiting_input');

  return {
    projectId: view.projectId,
    sync: {
      state: view.needsResync
        ? 'catching_up'
        : view.eventsTruncated
          ? 'partial'
          : 'live',
      currentCursor: view.currentCursor,
      lastSyncedAt: view.lastSyncedAt,
      detail: view.resyncReason,
    },
    runs,
    activeRunId: activeRun?.id || runs[runs.length - 1]?.id || null,
  };
}
